import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { BlattFuss, BlattHintergrund, BlattKopf } from './FilterBlatt';

/**
 * Knopf „Distanz“ in der Suchzeile am Rechner (28.10.2026), neben der Eventart. Vorher lag die
 * Distanz nur hinter „Filter“, obwohl sie in rund 3.400 Sitzungen je Quartal geändert wird. Vorbild
 * ist komoot: ein Menü direkt am Knopf mit festen Spannen und zwei Zahlenfeldern.
 *
 * Die Werte sind Texte wie im Filter-Blatt (`minKmInput`/`maxKmInput`); die Seite verzögert das
 * Laden selbst, deshalb wirkt jede Eingabe sofort. Eigene Spannen über einen Schieber mit zwei
 * Griffen (28.10.2026, Vorbild Strava, Runna): Zahlenfelder holten auf dem Handy die Tastatur über
 * das halbe Blatt. Ganz rechts heißt „beliebig“, ganz links „0“.
 */
const MAX = 300;
const SCHRITT = 10;
export interface DistanzSpanne { label: string; min: string; max: string }

interface Props {
  min: string;
  max: string;
  spannen: DistanzSpanne[];
  onWechsel: (min: string, max: string) => void;
  /** Trefferzahl für den Knopf unten im Blatt (Handy). */
  treffer?: number;
}

export const distanzWert = (min: string, max: string) =>
  min && max ? `${min}–${max} km` : min ? `ab ${min} km` : max ? `bis ${max} km` : '';

const DistanzFeld: React.FC<Props> = ({ min, max, spannen, onWechsel, treffer }) => {
  const [offen, setOffen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!offen) return;
    const zu = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOffen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOffen(false); };
    document.addEventListener('mousedown', zu);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', zu); document.removeEventListener('keydown', esc); };
  }, [offen]);

  const aktiv = !!(min || max);
  const lo = Math.min(Number(min) || 0, MAX);
  const hi = max ? Math.min(Number(max), MAX) : MAX;
  const setzeLo = (v: number) => onWechsel(v <= 0 ? '' : String(Math.min(v, hi - SCHRITT)), max);
  const setzeHi = (v: number) => onWechsel(min, v >= MAX ? '' : String(Math.max(v, lo + SCHRITT)));

  return (
    <div ref={ref} className="filter-pille-wrap">
      <button type="button" className={`filter-pille${aktiv ? ' filter-pille--an' : ''}`} aria-expanded={offen} aria-haspopup="dialog"
        onClick={() => setOffen(o => !o)}>
        {aktiv ? distanzWert(min, max) : 'Distanz'}
        {!aktiv && <ChevronDown size={15} aria-hidden />}
      </button>
      {aktiv && (
        <button type="button" className="filter-pille-x" onClick={() => { onWechsel('', ''); setOffen(false); }} aria-label="Distanz zurücksetzen"><X size={14} aria-hidden /></button>
      )}
      {offen && <BlattHintergrund onClose={() => setOffen(false)} />}
      {offen && (
        <div className="ort-menue distanz-menue" role="dialog" aria-label="Distanz">
          <BlattKopf titel="Distanz" onClose={() => setOffen(false)} />
          <div className="distanz-spannen" role="group" aria-label="Spanne">
            {spannen.map(s => {
              const an = s.min === min && s.max === max;
              return (
                <button key={s.label} type="button" aria-pressed={an} className={an ? 'ort-km ort-km--an' : 'ort-km'}
                  onClick={() => onWechsel(an ? '' : s.min, an ? '' : s.max)}>{s.label}</button>
              );
            })}
          </div>
          <div className="distanz-eigen">
            <div className="distanz-werte"><span>{lo} km</span><span>{hi >= MAX ? `${MAX}+ km` : `${hi} km`}</span></div>
            <div className="distanz-schieber" style={{ ['--lo' as string]: `${(lo / MAX) * 100}%`, ['--hi' as string]: `${(hi / MAX) * 100}%` }}>
              <input type="range" min={0} max={MAX} step={SCHRITT} value={lo} aria-label="Mindestens km" onChange={e => setzeLo(Number(e.target.value))} />
              <input type="range" min={0} max={MAX} step={SCHRITT} value={hi} aria-label="Höchstens km" onChange={e => setzeHi(Number(e.target.value))} />
            </div>
          </div>
          <BlattFuss treffer={treffer} onZuruecksetzen={aktiv ? () => onWechsel('', '') : undefined} onFertig={() => setOffen(false)} />
        </div>
      )}
    </div>
  );
};

export default DistanzFeld;
